import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useAuth } from './auth/AuthContext';
import LoadingScreen from './components/LoadingScreen';
import AcceptInvitePage from './pages/AcceptInvitePage';
import ResetPasswordPage from './pages/ResetPasswordPage';

const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const TermsAcceptancePage = lazy(() => import('./pages/TermsAcceptancePage'));
const ShopifyOnboardingPage = lazy(() => import('./pages/ShopifyOnboardingPage'));
const ApprovalsPage = lazy(() => import('./pages/ApprovalsPage'));
// Hidden admin-only style gallery — in no nav, reachable by direct URL only.
// Its own admin gate + redirect live in the page component.
const LookbookPage = lazy(() => import('./pages/LookbookPage'));

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

/**
 * Starts a login when rendered.
 *
 * `loginWithPassword` is the US-008 provider-neutral seam, not a claim about
 * credentials: under Keycloak it opens the hosted password form, under Scalekit
 * the hosted sign-in page offering Google and a one-time email code. The name
 * is kept because it has many call sites; what it does is "begin the hosted
 * auth flow".
 *
 * Used as catch-all for unauthenticated users on non-public routes
 * (e.g. SPA navigation from /accept-invite after completing setup) and
 * as the handler for the bookmarkable `/login` entry point.
 *
 * Preserves the current path + search so post-login the merchant lands
 * back on the page they were trying to reach (e.g. the Shopify
 * onboarding redirect from the OAuth callback).
 */
const TriggerLogin = () => {
  const { loginWithPassword } = useAuth();
  useEffect(() => {
    const target = window.location.pathname + window.location.search;
    void loginWithPassword(target || '/');
  }, []);
  return null;
};

const App = () => {
  const { loading, authenticated, user } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  // Custom-terms hard gate: a designated signer who owes a signature cannot
  // reach the dashboard until they accept. Set only for that signer, so other
  // users (including non-signer admins) are unaffected.
  if (authenticated && user?.termsAcceptanceRequired) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <TermsAcceptancePage />
      </Suspense>
    );
  }

  if (!authenticated) {
    return (
      <BrowserRouter>
        <SentryRoutes>
          {/* /accept-invite and /reset-password are registered in BOTH route
              tables, and must stay that way. Under Scalekit (US-009) the invite
              flow crosses the auth boundary WITHOUT changing path: a signed-out
              visitor lands here, clicks "Sign in to accept", returns from hosted
              auth to the same clean /accept-invite — now authenticated — and
              redeems. Drop either registration and one half of that single
              journey 404s into the catch-all. */}
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/login" element={<TriggerLogin />} />
          <Route path="*" element={<TriggerLogin />} />
        </SentryRoutes>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingScreen />}>
        <SentryRoutes>
          <Route path="/" element={<DashboardPage />} />
          {/* Page editor deep link. Same dashboard shell; catch-all must not
              send this path to `/` or a copied editor URL is lost on reload. */}
          <Route path="/brands/:brandId/pages/:pageId/edit" element={<DashboardPage />} />
          <Route path="/approvals" element={<ApprovalsPage />} />
          <Route path="/onboarding/shopify" element={<ShopifyOnboardingPage />} />
          <Route path="/lookbook" element={<LookbookPage />} />
          <Route path="/login" element={<Navigate to="/" replace />} />
          {/* The authenticated half of the pair above — this is where the
              Scalekit invite flow finishes. See the comment in the
              unauthenticated table. */}
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </SentryRoutes>
      </Suspense>
    </BrowserRouter>
  );
};

export default App;
